import React from 'react';
import { Box, CircularProgress, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FolderNotFound } from '../../icons';

interface FolderViewMessageProps {
  /** When true, shows a centered spinner instead of the text message. */
  loading?: boolean;
  message?: string;
  description?: string;
  /**
   * Marker prop read by ContentLayout (via `element.props.fullWidth`) to span
   * this element across the full width (md=12) instead of a single content
   * column. It is intentionally never read inside this component.
   * @see ContentLayout `isFullWidth`
   */
  // eslint-disable-next-line react/no-unused-prop-types
  fullWidth?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
    paddingBottom: theme.spacing(8),
  },
  messageTitle: {
    color: 'var(--text-primary-dark)',
    fontWeight: 600,
  },
  messageDescription: {
    color: 'var(--text-primary-dark-2)',
  },
  messageIcon: {
    marginBottom: theme.spacing(2),
  },
}));

/**
 * Full-width status row (loading / error / empty) shown in place of a folder's
 * contents. Rendered as a sibling of the content items so ContentLayout places
 * it in its own full-width grid cell.
 */
const FolderViewMessage: React.FC<FolderViewMessageProps> = ({
  loading,
  message,
  description,
}) => {
  const classes = useStyles();

  return loading ? (
    <Box className={classes.loadingContainer}>
      <CircularProgress />
    </Box>
  ) : (
    <Box className={classes.messageContainer}>
      <FolderNotFound className={classes.messageIcon} />
      <Typography variant="body2" className={classes.messageTitle}>
        {message}
      </Typography>
      <Typography variant="body2" className={classes.messageDescription}>
        {description}
      </Typography>
    </Box>
  );
};

export default FolderViewMessage;
